import { TrashIcon } from "@heroicons/react/24/outline";
import {
  Button,
  getInput,
  IconButton,
} from "../../../../../../../components/ui";
import { useFormik } from "formik";
import { useEffect } from "react";
import { GET_SHIPMENT_DOCUMENT_UPLOAD_URLS } from "../../../../../../../graphql/mutation/ShipmentDocument";
import { ALLOWED_DOCUMENT_UPLOAD_ACCEPT } from "../../../../../../../util/allowedDocumentUploadAccept";
import useDocumentUpload from "../../../../../../../hooks/useDocumentUpload";

const AddDocuments = props => {
  const { closeModal, shipment, isOpen } = props;

  const {
    upload,
    retry,
    loading,
    progress,
    isUploading,
    failed,
    reset,
  } = useDocumentUpload({
    query: GET_SHIPMENT_DOCUMENT_UPLOAD_URLS,
    refetchQueries: ["FetchShipmentDocuments"],
  });

  const formik = useFormik({
    initialValues: {
      files: [],
    },
    enableReinitialize: true,
  });

  const handleClick = async () => {
    const files = formik?.values?.files ?? [];
    await upload({
      variables: {
        shipmentId: shipment?.id,
        filenames: files.map(f => f.name),
      },
      files,
    });
  };

  const handleDelete = index => {
    const { values: { files = [] } = {} } = formik || {};
    const newDocuments = files?.filter((item, i) => i !== index);
    formik.setFieldValue("files", newDocuments);
  };

  const inputs = [
    {
      label: "Attach File(s)",
      type: "img",
      accept: ALLOWED_DOCUMENT_UPLOAD_ACCEPT,
      isMultiSelect: true,
      uploadType: "file(s)",
      w: "w-full",
      h: "h-36",
      disabled: isUploading || loading,
      onChange: e => {
        const files = e.target.files;
        const newArray = [];
        for (let i = 0; i < files.length; i++) {
          newArray.push(files[i]);
        }
        formik.setFieldValue("files", [...formik.values.files, ...newArray]);
      },
    },
  ];

  useEffect(() => {
    formik?.resetForm();
    reset();
  }, [isOpen]);

  const uploadSuccess = progress === 100;

  return (
    <div className="flex w-full flex-col p-8">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          Upload Documents for {shipment?.id}
        </div>
        <div className="flex flex-row">
          <Button
            action={uploadSuccess ? "primary" : "default"}
            className={`mr-8 ${uploadSuccess ? "bg-green-600 hover:bg-green-700" : ""}`}
            label={uploadSuccess ? "Done" : "Cancel"}
            onClick={() => closeModal()}
          />
          {!uploadSuccess && failed.length === 0 && (
            <Button
              label={loading || isUploading ? "Uploading" : "Upload Record"}
              disabled={
                loading || isUploading || formik?.values?.files?.length === 0
              }
              onClick={handleClick}
            />
          )}
        </div>
      </div>
      {isUploading && (
        <div className="mt-6 w-full">
          <div className="mb-1 flex justify-between">
            <span className="text-lg font-bold tracking-wide text-gray-600">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-black transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
      {failed.length > 0 && (
        <div className="mt-6 rounded-md bg-red-50 p-4 text-red-800">
          <p className="font-semibold">
            {failed.length} upload(s) failed
          </p>
          <ul>
            {failed.map(({ file = {} }) => (
              <li key={file.size}>{file.name}</li>
            ))}
          </ul>
          <Button className="mt-2" label="Retry Failed" onClick={retry} />
        </div>
      )}
      <div className="grid w-full grid-cols-1 pb-10 pt-20">
        <div className="flex">
          {inputs?.map((item, index) => {
            const inputProps = {
              ...item,
            };
            return (
              <div key={index} className={`w-full ${item?.className}`}>
                {getInput(inputProps)}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex flex-col">
          <div className="flex-1">Selected File(s)</div>
          <div className="mt-4">
            {formik?.values?.files?.map((item, index) => {
              return (
                <div key={index} className="flex flex-row gap-4">
                  <IconButton variant="clean" title="Delete Attachment">
                    <TrashIcon
                      className={`h-4 w-4 cursor-pointer text-gray-400`}
                      onClick={() => {
                        handleDelete(index);
                      }}
                    />
                  </IconButton>
                  <div className="flex-1">{item?.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDocuments;
